import { motion, AnimatePresence } from "motion/react";
import { useState, useEffect } from "react";

import huggingfaceLogo from "../assets/BackedBy/HuggingFace.png";
import whatsappLogo from "../assets/BackedBy/WhatsApp.png";
import woltLogo from "../assets/BackedBy/Wolt.png";

import { useReducedMotion } from "./ui/use-reduced-motion";

const companies = ["Hugging Face", "WhatsApp", "Wolt"];

export default function BackedBy() {
  const prefersReducedMotion = useReducedMotion();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % companies.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="w-full overflow-hidden py-16 md:py-24 lg:py-32">
      <div className="mx-auto max-w-[1600px] px-4 md:px-8 lg:px-16">
        <div className="grid items-center gap-6 md:gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left side - Text content */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { duration: 0.8, ease: "easeOut" }
            }
            className="space-y-4 md:space-y-6"
          >
            <h2
              className="leading-[1.1] tracking-tight text-white"
              style={{ fontSize: "var(--text-section)", fontWeight: "600" }}
            >
              Backed by top VCs and founders{" "}
              <span className="inline-block whitespace-nowrap">
                from{" "}
                <span
                  className="relative inline-block"
                  style={{ minWidth: "200px", verticalAlign: "bottom" }}
                >
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                      className="inline-block"
                      style={{ color: "#6b96ff" }}
                    >
                      {companies[currentIndex].split("").map((char, index) => (
                        <motion.span
                          key={`${currentIndex}-${index}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            duration: 0.05,
                            delay: index * 0.05,
                            ease: "linear",
                          }}
                          style={{ display: "inline-block" }}
                        >
                          {char === " " ? "\u00A0" : char}
                        </motion.span>
                      ))}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </span>
            </h2>
            <p
              className="max-w-2xl leading-relaxed text-gray-300"
              style={{ fontSize: "var(--text-lead)" }}
            >
              We've raised €4M to reimagine design from the ground up.
            </p>
          </motion.div>

          {/* Right side - Logo placeholders */}
          <motion.div
            initial={prefersReducedMotion ? {} : { opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={
              prefersReducedMotion
                ? { duration: 0 }
                : { duration: 0.8, ease: "easeOut", delay: 0.2 }
            }
            className="relative"
          >
            <div className="relative mx-auto h-[400px] max-w-[500px] lg:mr-0 lg:ml-auto">
              {/* Top right - WhatsApp logo */}
              <motion.img
                className="absolute top-0 right-[0px] h-40 object-contain"
                src={whatsappLogo}
                alt="WhatsApp"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              />

              {/* Middle left - Hugging Face emoji logo */}
              <motion.img
                className="absolute top-[80px] left-[0px] h-40 object-contain"
                src={huggingfaceLogo}
                alt="Hugging Face"
                whileHover={{ scale: 1.1, rotate: -5 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              />

              {/* Bottom center-right - Wolt logo */}
              <motion.img
                className="absolute right-[50px] bottom-0 h-40 object-contain lg:right-[100px]"
                src={woltLogo}
                alt="Wolt"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
